// Every language on this page is read into these types and written back out of them, so a reader and a writer never
// know about each other: three readers and three writers make nine conversions, and none of them is a pair.

export type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };

export interface Meta {
  title?: string;
  description?: string;
  default?: JsonValue;
}

// What JSON Schema can say of a value whatever its type, which is why every node carries them and no kind is theirs.
export interface Applicators {
  not?: Schema;
  // `then` binds a value `if` accepts and `else` one it refuses; `if` beside neither of them says nothing at all.
  if?: Schema;
  then?: Schema;
  else?: Schema;
}

export interface UnknownSchema extends Meta, Applicators {
  kind: "unknown";
}

export interface NeverSchema extends Meta, Applicators {
  kind: "never";
}

export interface NullSchema extends Meta, Applicators {
  kind: "null";
}

export interface BooleanSchema extends Meta, Applicators {
  kind: "boolean";
}

export interface NumberSchema extends Meta, Applicators {
  kind: "number";
  integer?: boolean;
  minimum?: number;
  maximum?: number;
  exclusiveMinimum?: number;
  exclusiveMaximum?: number;
  multipleOf?: number;
}

export interface StringSchema extends Meta, Applicators {
  kind: "string";
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  format?: string;
}

export interface LiteralSchema extends Meta, Applicators {
  kind: "literal";
  value: JsonValue;
}

export interface EnumSchema extends Meta, Applicators {
  kind: "enum";
  values: JsonValue[];
}

export interface ArraySchema extends Meta, Applicators {
  kind: "array";
  items: Schema;
  // A tuple: the nth element answers to the nth schema, and `items` is whatever is allowed past the end of the list.
  prefix?: Schema[];
  minItems?: number;
  maxItems?: number;
  uniqueItems?: boolean;
  // How many items `contains` has to accept: at least one unless `minContains` says otherwise, and at most any.
  contains?: Schema;
  minContains?: number;
  maxContains?: number;
}

export interface Property {
  name: string;
  schema: Schema;
  required: boolean;
}

export interface ObjectSchema extends Meta, Applicators {
  kind: "object";
  properties: Property[];
  // `false` closes the object, a schema is what every other key has to be, and absent leaves it open and unchecked.
  // A map with no declared properties is this and nothing else, which is why there is no separate record kind.
  additional?: Schema | false;
  keyPattern?: string;
  // A key any of these matches is held to that pattern's schema, and is then no key `additional` has a say over.
  patterns?: PatternProperty[];
  minProperties?: number;
  maxProperties?: number;
  dependencies?: Dependency[];
}

export interface PatternProperty {
  pattern: string;
  schema: Schema;
}

// `dependentRequired`, kept as a list of pairs rather than an object keyed by name, as a key can be `__proto__`.
export interface Dependency {
  name: string;
  requires: string[];
}

export interface UnionSchema extends Meta, Applicators {
  kind: "union";
  options: Schema[];
  // `oneOf`, where a value one option accepts has to be refused by all the others.
  exclusive?: boolean;
}

export interface IntersectionSchema extends Meta, Applicators {
  kind: "intersection";
  parts: Schema[];
}

export interface RefSchema extends Meta, Applicators {
  kind: "ref";
  name: string;
}

export type Schema =
  | UnknownSchema
  | NeverSchema
  | NullSchema
  | BooleanSchema
  | NumberSchema
  | StringSchema
  | LiteralSchema
  | EnumSchema
  | ArraySchema
  | ObjectSchema
  | UnionSchema
  | IntersectionSchema
  | RefSchema;

export interface Definition {
  name: string;
  schema: Schema;
}

// Named models are kept whole rather than inlined, because a model that refers to itself has no inlined form at all.
export interface SchemaDocument {
  root: Schema;
  defs: Definition[];
}

// A reader answers with whatever it could make of the source and whatever it could not; nothing here throws.
export interface ReadResult {
  document: SchemaDocument | null;
  errors: SourceError[];
}

export interface SourceError {
  message: string;
  from?: number;
  to?: number;
}

export function lookup(doc: SchemaDocument, name: string): Schema | undefined {
  return doc.defs.find((def) => def.name === name)?.schema;
}

// Follows a chain of refs to the schema that actually says something, and gives up rather than circle forever.
export function resolve(schema: Schema, doc: SchemaDocument): Schema {
  const seen = new Set<string>();
  let current = schema;
  while (current.kind === "ref") {
    if (seen.has(current.name)) return { kind: "unknown" };
    seen.add(current.name);
    const next = lookup(doc, current.name);
    if (!next) return { kind: "unknown" };
    current = next;
  }
  return current;
}

// Flattens nested unions and drops the duplicates a merge leaves behind, since `string | string` is one choice.
export function union(options: Schema[]): Schema {
  const flat: Schema[] = [];
  for (const option of options) {
    // A oneOf or a union with a condition of its own is one branch, and spilling its options would lose what it said.
    if (option.kind === "union" && !option.exclusive && !conditional(option)) flat.push(...option.options);
    else if (option.kind !== "never") flat.push(option);
  }

  const unique: Schema[] = [];
  for (const option of flat) {
    if (!unique.some((kept) => same(kept, option))) unique.push(option);
  }

  if (unique.length === 0) return { kind: "never" };
  if (unique.length === 1) return unique[0];
  return { kind: "union", options: unique };
}

// `oneOf`, which is neither flattened nor deduplicated: two options the same are two a value cannot pick between.
export function exclusive(options: Schema[]): Schema {
  if (options.length === 0) return { kind: "never" };
  if (options.length === 1) return options[0];
  return { kind: "union", options, exclusive: true };
}

export function conditional(schema: Schema): boolean {
  return schema.not !== undefined || schema.if !== undefined;
}

// A table read only for the keys it was written with, so a name like `constructor` finds nothing in it.
export function own<T>(table: { [key: string]: T }, key: string): T | undefined {
  return Object.hasOwn(table, key) ? table[key] : undefined;
}

export function nullable(schema: Schema): Schema {
  return union([schema, { kind: "null" }]);
}

// What is left once null is taken out of a union, which is how `Optional[str]` reads back as a string again.
export function withoutNull(schema: Schema): Schema {
  if (schema.kind !== "union") return schema;
  return union(schema.options.filter((option) => option.kind !== "null"));
}

export function isNullable(schema: Schema): boolean {
  if (schema.kind === "null") return true;
  return schema.kind === "union" && schema.options.some((option) => option.kind === "null");
}

// Structural, and only ever asked of two schemas being folded into one union, where a deep compare is cheap.
export function same(a: Schema, b: Schema): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

// The name a reader gives a schema when it has to say what was expected; short enough to sit in an error message.
export function describe(schema: Schema): string {
  switch (schema.kind) {
    case "unknown":
      return "anything";
    case "never":
      return "nothing";
    case "null":
      return "null";
    case "boolean":
      return "a boolean";
    case "number":
      return schema.integer ? "an integer" : "a number";
    case "string":
      return schema.format ? `a string (${schema.format})` : "a string";
    case "literal":
      return JSON.stringify(schema.value) ?? "a literal";
    case "enum":
      return `one of ${schema.values.map((value) => JSON.stringify(value)).join(", ")}`;
    case "array":
      return "an array";
    case "object":
      return "an object";
    case "union":
      return schema.options.map(describe).join(" or ");
    case "intersection":
      return schema.parts.map(describe).join(" and ");
    case "ref":
      return schema.name;
  }
}

// The JSON type name a value carries, which is the other half of every type error on the page.
export function typeOf(value: JsonValue): string {
  if (value === null) return "null";
  if (Array.isArray(value)) return "an array";
  switch (typeof value) {
    case "boolean":
      return "a boolean";
    case "number":
      return Number.isInteger(value) ? "an integer" : "a number";
    case "string":
      return "a string";
    default:
      return "an object";
  }
}
